import { AccessToken } from '../services/access-token';
import { ApiCall } from '../services/http-service/types/ApiCall';
import httpService, { authHttpService } from '../services/http-service';
import { UserImpersonation } from '../services/user-impersonation';
import { ForceChangePassword } from '../services/force-change-password';
import { UserCurrentCompany } from '../services/user-company';
import { RefreshableHttpServiceConfigurator } from '../services/http-service/refreshable-http-service-configurator';
import { ChangePasswordParams } from './types/ChangePasswordParams';
import { UserCompany } from './types/UserCompany.types';

declare global {
  interface Window {
    FS: any;
  }
}

/**
 * Change password with confirm old one
 */
export const changePassword: ApiCall<ChangePasswordParams, void> = async ({ params, abortSignal }) => {
  await authHttpService.put('/api/v1/password', params, abortSignal);
};

/**
 * Reset password with new password only
 */
export const resetPassword = async (params: {
  password: string;
  reset_password_token: string;
}) => {
  const res = await authHttpService.put('/api/v1/reset-password',
    params
  );
  return res.data;
};

/**
 * Send email with reset password token
 */
export const requestResetPassword: ApiCall<
  {
    email: string;
    redirect_url: string;
  },
  void
> = async ({ params, abortSignal }) => {
  const res = await authHttpService.post('/api/v1/reset-password',
    params,
    abortSignal
  );
  return res.data;
};

/**
 * Signin request with email and password
 */
export const signIn = async (values: { email: string; password: string }) => {
  try {
    const authResponse = await authHttpService.post('/api/v1/email-auth',
      { ...values }
    );
    const userResponse = await httpService.get('/api/v2/profile/extended');
    ForceChangePassword.setChangePasswordValue(authResponse.data.force_change_password);

    return userResponse.data;
  } catch (err: any) {
    const errors = err ? err.errors : err;
    throw errors;
  }
};

/**
 * OAuth AAD Signin request
 */
export const oauthSignIn: ApiCall<{ code: string; redirect_uri: string }, void> =
  async ({ params, abortSignal }) => {
    try {
      await authHttpService.post(
        '/api/v1/oauth/azure_auth',
        params,
        abortSignal
      );
    } catch (err: any) {
      const errors = err ? err.errors : err;
      throw errors;
    }
  };

/**
 * Invalidate token and close session
 */
export const signOut = async () => {
  const headers = RefreshableHttpServiceConfigurator.getRefreshTokenHeaders();
  await authHttpService.delete('/api/v1/token', null, undefined, headers).catch(() => ({}));
  AccessToken.clearTokens();
  UserImpersonation.clearImpersonationData();
};

/**
 * Check if existing token is valid and return user data
 */
export const checkUser = async () => {
  try {
    const res = await httpService.get('/api/v2/profile/extended');

    return res.data;
  } catch (error) {
    AccessToken.clearTokens();
    UserImpersonation.clearImpersonationData();
    UserCurrentCompany.clearCompany();
    throw error;
  }
};

/**
 * Refresh access token
 */
export const refreshAccessToken = () => {
  try {
    const headers = RefreshableHttpServiceConfigurator.getRefreshTokenHeaders();
    return authHttpService.post('/api/v1/token', null, undefined, headers);
  } catch (error) {
    AccessToken.clearTokens();
    UserImpersonation.clearImpersonationData();
    UserCurrentCompany.clearCompany();
    throw error;
  }
};

/**
 * Http request for impersonate
 *
 * @param userId
 * @param portalId - query params with selected portal
 */
export const impersonateUser = async (
  userId: number,
  portalId: number
) => {
  const headers = RefreshableHttpServiceConfigurator.getRefreshTokenHeaders();
  const { data } = await httpService.post(
    `/api/v2/users/${userId}/impersonate?portal=${portalId}`,
    null, undefined, headers
  );
  return data;
};

/**
 * remove user from impersonate
 */
export const backFromImpersonate = async () => {
  const headers = RefreshableHttpServiceConfigurator.getRefreshTokenHeaders();
  await authHttpService
    .put('/api/v1/token/rollback', null, undefined, headers);
};

type ImpersonateUserV3Request = {
  userId: number;
};

export const impersonateUserV3: ApiCall<ImpersonateUserV3Request, void> =
  async ({ params, abortSignal }) => {
    const headers = RefreshableHttpServiceConfigurator.getRefreshTokenHeaders();
    const { data } = await httpService.post(
      `/user-management/api/v3/users/${params.userId}/impersonate`,
      null,
      abortSignal,
      headers
    );
    return data;
  };

export const getUserCompanies: ApiCall<null, UserCompany[]> = async ({ abortSignal }) => {
  const { data } = await httpService.get(
    '/api/v3/account/authorization/companies',
    null,
    abortSignal,
  );
  return data;
};
